import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DialogResult } from '../../shared/models/dialog-result';
import { Observable } from 'rxjs';
import { ProPadTypeCd } from '@xpo-ltl/sdk-common';

interface DialogInput {
  type: string;
  value: string;
  label: string;
  valueKey: string;
  valueName: string;
  controlName: string;
  required?: boolean;
  options?: [] | Observable<[]>;
}

@Component({
  moduleId: module.id,
  selector: 'confirm-request-dialog',
  templateUrl: 'confirm-request-dialog.component.html',
  styleUrls: ['confirm-request-dialog.component.scss'],
})
export class ConfirmRequestDialogComponent implements OnInit {
  requestDialogForm: FormGroup;
  confirmMessage: string;
  confirmText: string;
  cancelText: string;
  title: string;
  reasonTitle: string;
  paletteColour: string;
  quantities: [];
  inputs: DialogInput[];
  reasonMax = 500;
  reasonText: string;
  reasonRequired: boolean;
  labelTypes: { type: string; imageUrl: string; name: string }[] = [
    { type: ProPadTypeCd.CA_BLUE_YELLOW, imageUrl: 'blue-yellow-label.png', name: 'CA, Blue & Yellow' },
    { type: ProPadTypeCd.US_BLUE_YELLOW, imageUrl: 'blue-yellow-label.png', name: 'US, Blue & Yellow' },
    { type: ProPadTypeCd.CA_BLUE, imageUrl: 'blue-label.png', name: 'CA, Blue' },
    { type: ProPadTypeCd.US_BLUE, imageUrl: 'blue-label.png', name: 'US, Blue' },
    { type: ProPadTypeCd.YELLOW, imageUrl: 'yellow-label.png', name: 'Yellow' },
  ];
  noImage: boolean;

  constructor(
    public dialogRef: MatDialogRef<ConfirmRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    const {
      confirmMessage,
      confirmText,
      cancelText,
      title,
      reasonTitle,
      paletteColour,
      quantities,
      reasonRequired,
      inputs,
    } = this.dialogData;
    this.confirmMessage = confirmMessage;
    this.confirmText = confirmText;
    this.cancelText = cancelText;
    this.title = title;
    this.reasonTitle = reasonTitle;
    this.paletteColour = paletteColour;
    this.quantities = quantities;
    this.reasonRequired = reasonRequired;
    this.inputs = inputs;
    this.requestDialogForm = this.createForm();
  }

  private createForm() {
    const validators: Validators[] =
      this.reasonRequired || (this.quantities && this.quantities.length > 0)
        ? [Validators.required, Validators.maxLength(this.reasonMax), this.noWhitespaceValidator]
        : [];
    const dialogForm: FormGroup = this.fb.group({
      reasonText: [{ value: '', disabled: false }, validators],
    });

    if (this.inputs && this.inputs.length > 0) {
      this.inputs.forEach((control: DialogInput) => {
        const inputValidators: ValidatorFn | ValidatorFn[] = control.required ? Validators.required : [];
        dialogForm.addControl(control.controlName, new FormControl(control.value, inputValidators));
      });
    }

    return dialogForm;
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  getImageName(type) {
    const matchingLabelType = this.labelTypes.find((x) => x.type === type);
    if (matchingLabelType) {
      return matchingLabelType.imageUrl;
    } else {
      this.noImage = true;
      return null;
    }
  }

  confirm() {
    const reason = this.requestDialogForm.controls['reasonText'].value;
    const dataForm = this.requestDialogForm.getRawValue();
    const result: DialogResult = { reason: reason, dataForm: dataForm, confirmed: true };
    this.dialogRef.close(result);
  }

  cancel() {
    const result: DialogResult = { confirmed: false };
    this.dialogRef.close(result);
  }

  getLabelName(type) {
    const matchingLabelType = this.labelTypes.find((x) => x.type === type);
    return matchingLabelType ? matchingLabelType.name : '';
  }
}
